import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks/shared/useAccessToken";
import { TenantApi } from "@/openapi/generated";
import type { UpdateTenantRequest } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import type { TenantWithRegion } from "@/lib/utils/tenant-account";

export const useTenantUpdate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (updateTenantRequest: UpdateTenantRequest) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(TenantApi, accessToken);

      return service.apiAccountPut({
        updateTenantRequest,
        xTenantID: Number(updateTenantRequest.tenantId),
      });
    },
    onSuccess: async (_, variables) => {
      const nextState =
        typeof variables.state === "string" ? variables.state.trim() : "";
      const nextCountry =
        typeof variables.country === "string" ? variables.country.trim() : "";

      queryClient.setQueriesData(
        { queryKey: [QueryKeys.Tenant, variables.tenantId] },
        (current: TenantWithRegion | undefined) => {
          if (!current) return current;
          return {
            ...current,
            state: nextState.length > 0 ? nextState : current.state,
            country: nextCountry.length > 0 ? nextCountry : current.country,
            driveWebhookUrl: variables.driveWebhookUrl ?? current.driveWebhookUrl,
          };
        },
      );

      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Tenant, variables.tenantId],
      });
      toast({
        title: "Account updated",
        description: "Account settings updated successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update account settings";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, mutateAsync, isPending, isError, isSuccess };
};

